import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import NoIndex from "../../../components/NoIndex";
import "../../whatsapp/WhatsApp.css";
import useCampaigns from "../../../services/Whatsapp/useCampaigns";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import { ChevronDown } from "lucide-react";

const CardSkeleton = () => (
  <div className="border rounded-xl p-6 bg-white space-y-4">
    <div className="shimmer h-5 w-40"></div>
    <div className="shimmer h-4 w-full"></div>
    <div className="shimmer h-4 w-3/4"></div>
    <div className="shimmer h-4 w-1/2"></div>
  </div>
);

const StatSkeleton = () => (
  <div className="border rounded-md p-4 text-center space-y-2">
    <div className="shimmer h-3 w-20 mx-auto"></div>
    <div className="shimmer h-6 w-10 mx-auto"></div>
  </div>
);

const CampaignView = ({ userID }) => {
  const location = useLocation();

  const [campaignId, setCampaignId] = useState("");
  const [campaignPhoneId, setCampaignPhoneId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [animate, setAnimate] = useState(false);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const { loading, getCampaigns } = useCampaigns();
  const { templates, fetchTemplatesByUser } = useTemplates();
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!campaignPhoneId) return;

    const loadData = async () => {
      const data = await getCampaigns(campaignPhoneId);
      setCampaigns(data || []);
    };

    loadData();
  }, [campaignPhoneId]);

  useEffect(() => {
    fetchTemplatesByUser();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setCampaignId(searchParams.get("campaignId"));
    setCampaignPhoneId(searchParams.get("phoneId"));
  }, [location.search]);

  const selectedCampaign = useMemo(() => {
    return campaigns.find((c) => c.id === campaignId);
  }, [campaigns, campaignId]);

  const template = useMemo(() => {
    if (!selectedCampaign) return null;

    return templates.find(
      (t) =>
        t.name?.toLowerCase() === selectedCampaign.template_id?.toLowerCase() ||
        t.template_name?.toLowerCase() ===
          selectedCampaign.template_id?.toLowerCase(),
    );
  }, [templates, selectedCampaign]);

  useEffect(() => {
    setAnimate(false);
    setTimeout(() => setAnimate(true), 50);
  }, [template]);

  const decodeBase64 = (str) => {
    if (!str) return "";
    try {
      return atob(str);
    } catch {
      return str;
    }
  };

  const replaceVariablesBody = (text) => {
    const replacements = ["John Doe", "Messaging"];
    return text?.replace(
      /{{(\d+)}}/g,
      (_, index) => replacements[index - 1] || "",
    );
  };

  const headerText = decodeBase64(
    template?.components?.find((c) => c.type === "HEADER")?.text,
  );
  const bodyText = decodeBase64(
    template?.components?.find((c) => c.type === "BODY")?.text,
  );
  const footerText = decodeBase64(
    template?.components?.find((c) => c.type === "FOOTER")?.text,
  );
  const buttons =
    template?.components?.find((c) => c.type === "BUTTONS")?.buttons || [];
  const headerImage = template?.components?.find((c) => c.format === "IMAGE")
    ?.example?.header_handle?.[0];
  const headerVideo = template?.components?.find((c) => c.format === "VIDEO")
    ?.example?.header_handle?.[0];

  return (
    <>
      <NoIndex />

      <div className="min-h-screen overflow-hidden p-5 montserrat-font-1">
        <div className="max-w-8xl mx-auto montserrat-font-1">
          <h1 className="text-2xl font-semibold mb-6">Campaign Details</h1>

          {loading || !selectedCampaign ? (
            <div className="space-y-6">
              <CardSkeleton />

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <StatSkeleton key={i} />
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                <CardSkeleton />
                <CardSkeleton />
              </div>
            </div>
          ) : !selectedCampaign ? (
            <div className="text-center text-gray-500 mt-10">shivam</div>
          ) : (
            <div className="border border-gray-300 rounded-xl p-6 bg-white montserrat-font-1">
              <div
                onClick={toggleDropdown}
                className="flex justify-between items-center cursor-pointer bg-gray-100 px-4 py-3 rounded-lg"
              >
                <span className="font-semibold">Campaign Info</span>
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-300 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </div>

              {isOpen && (
                <div className="mt-4 overflow-x-auto montserrat-font-1">
                  <table className="min-w-full border border-gray-200 montserrat-font-1">
                    <tbody className="divide-y montserrat-font-1">
                      <tr>
                        <th className="p-3 text-left font-semibold">
                          Campaign Name
                        </th>
                        <td className="p-3">
                          {selectedCampaign.campaign_name}
                        </td>
                        <th className="p-3 text-left font-semibold">
                          Template
                        </th>
                        <td className="p-3">{selectedCampaign.template_id}</td>
                      </tr>
                      <tr>
                        <th className="p-3 text-left font-semibold">Start</th>
                        <td className="p-3">
                          {selectedCampaign.campaign_start}
                        </td>
                        <th className="p-3 text-left font-semibold">End</th>
                        <td className="p-3">{selectedCampaign.campaign_end}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* ===== Stats ===== */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6 montserrat-font-1">
                {[
                  ["Total Read", selectedCampaign.read, "blue"],
                  ["Total Delivered", selectedCampaign.delivered, "green"],
                  ["Accepted", selectedCampaign.accepted, "cyan"],
                  ["Failed", selectedCampaign.failed, "red"],
                  ["Total Responded", selectedCampaign.responded, "yellow"],
                  ["Total Sent", selectedCampaign.sent, "purple"],
                ].map(([label, value, color], i) => (
                  <div
                    key={i}
                    className={`border border-${color}-500 bg-${color}-50 text-${color}-600 text-center rounded-md p-4`}
                  >
                    <p className="text-sm">{label}</p>
                    <p className="font-bold text-2xl mt-1">{value ?? 0}</p>
                  </div>
                ))}
              </div>

              {/* ===== Template Section ===== */}
              {template && (
                <div className="grid lg:grid-cols-2 gap-8 mt-8 montserrat-font-1">
                  {/* Template Details — decoded */}
                  <div className="border rounded-lg p-4 montserrat-font-1">
                    <h2 className="font-semibold text-lg mb-4">
                      Template Details
                    </h2>

                    <p>
                      <strong>Name:</strong> {template.name}
                    </p>
                    <p>
                      <strong>Category:</strong> {template.category}
                    </p>
                    <p>
                      <strong>Status:</strong> {template.status}
                    </p>
                    <p>
                      <strong>Language:</strong> {template.language}
                    </p>

                    <div className="mt-4 space-y-3 montserrat-font-1">
                      {headerText && (
                        <div>
                          <h5 className="font-semibold text-sm text-gray-500">
                            HEADER
                          </h5>
                          <p>{headerText}</p>
                        </div>
                      )}
                      {bodyText && (
                        <div>
                          <h5 className="font-semibold text-sm text-gray-500">
                            BODY
                          </h5>
                          <p className="whitespace-pre-wrap">{bodyText}</p>
                        </div>
                      )}
                      {footerText && (
                        <div>
                          <h5 className="font-semibold text-sm text-gray-500">
                            FOOTER
                          </h5>
                          <p>{footerText}</p>
                        </div>
                      )}
                      {buttons.length > 0 && (
                        <div>
                          <h5 className="font-semibold text-sm text-gray-500">
                            BUTTONS
                          </h5>
                          <div className="space-y-1 mt-1">
                            {buttons.map((btn, i) => (
                              <p key={i} className="text-sm text-gray-700">
                                • {btn.text}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ===== Mobile Preview — old classnames ===== */}
                  <div className="flex justify-center items-start mt-4 montserrat-font-1">
                    <div
                      className="mobile-card montserrat-font-1"
                      style={{ position: "relative", marginTop: "1rem" }}
                    >
                      <div className="mobile-btn1"></div>
                      <div className="mobile-btn2"></div>
                      <div className="mobile-btn3"></div>
                      <div className="mobile-btn4"></div>

                      <div className="mobile-card-int montserrat-font-1">
                        <div className="bubble-container">
                          <div
                            className={`message-bubble ${animate ? "message-bubble-animate" : ""}`}
                          >
                            {loading || !template ? (
                              <div className="space-y-3 w-full">
                                <div className="shimmer h-32 w-full rounded-lg"></div>
                                <div className="shimmer h-4 w-5/6 rounded"></div>
                                <div className="shimmer h-4 w-2/3 rounded"></div>
                                <div className="shimmer h-3 w-1/3 rounded"></div>
                              </div>
                            ) : (
                              <>
                                {/* Video */}
                                {headerVideo && (
                                  <video width="90%" height="200px" controls>
                                    <source
                                      src={headerVideo}
                                      type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                  </video>
                                )}

                                {/* Image */}
                                {!headerVideo && headerImage && (
                                  <img
                                    src={headerImage}
                                    alt="Template"
                                    style={{
                                      maxWidth: "90%",
                                      borderRadius: "8px",
                                    }}
                                  />
                                )}

                                {/* HEADER — decoded */}
                                <div className="header-text">{headerText}</div>

                                {/* BODY — decoded */}
                                <div className="body-text py-1">
                                  {replaceVariablesBody(bodyText)}
                                </div>

                                {/* FOOTER — decoded */}
                                <div className="footer-text">{footerText}</div>

                                <div className="footer-text text-end">
                                  05:27 PM
                                </div>
                              </>
                            )}
                          </div>

                          {/* BUTTONS */}
                          {buttons.map((button, index) => (
                            <div
                              key={index}
                              className={`button-bubble ${animate ? "button-bubble-animate" : ""}`}
                            >
                              <div className="button-text text-success text-center mb-1">
                                {button.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mobile-top montserrat-font-1">
                        <div className="mobile-camera">
                          <div className="mobile-int"></div>
                        </div>
                        <div className="mobile-speaker"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CampaignView;
